import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  ClipboardIcon,
  EnvelopeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const EmployeeCredentialsModal = ({
  isOpen,
  onClose,
  credentials,
  employeeName
}) => {
  const { t } = useTranslation(['common', 'employees']);
  const [showPassword, setShowPassword] = useState(false);

  if (!isOpen || !credentials) return null;

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-secondary-900 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-3xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-display font-semibold text-secondary-900">
                    {t('employeeCreatedSuccessfully', { defaultValue: 'Employee Created Successfully' })}
                  </h3>
                  <p className="text-sm text-secondary-600">
                    {employeeName} {t('canNowLogin', { defaultValue: 'can now login to the system' })}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-secondary-400 hover:text-secondary-600 transition-colors duration-200"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Credentials Section */}
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <EnvelopeIcon className="h-5 w-5 mr-2 text-blue-600" />
                  {t('loginCredentials', { defaultValue: 'Login Credentials' })}
                </h4>

                <div className="space-y-4">
                  {/* Employee ID */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        {t('employeeId', { defaultValue: 'Employee ID' })}
                      </p>
                      <p className="text-lg font-mono font-bold text-gray-900">
                        {credentials.employeeId}
                      </p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(credentials.employeeId, 'Employee ID')}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Copy Employee ID"
                    >
                      <ClipboardIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Email */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        {t('email', { defaultValue: 'Email' })}
                      </p>
                      <p className="text-sm font-medium text-gray-900">
                        {credentials.email}
                      </p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(credentials.email, 'Email')}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Copy Email"
                    >
                      <ClipboardIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Temporary Password */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700">
                        {t('temporaryPassword', { defaultValue: 'Temporary Password' })}
                      </p>
                      <div className="flex items-center space-x-2">
                        <p className="text-lg font-mono font-bold text-gray-900">
                          {showPassword ? credentials.temporaryPassword : '••••••••••••'}
                        </p>
                        <button
                          onClick={() => setShowPassword(!showPassword)}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          title={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-4 w-4" />
                          ) : (
                            <EyeIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => copyToClipboard(credentials.temporaryPassword, 'Password')}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Copy Password"
                    >
                      <ClipboardIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Important Notes */}
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-6 border border-yellow-200">
                <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <CheckCircleIcon className="h-5 w-5 mr-2 text-yellow-600" />
                  {t('importantNotes', { defaultValue: 'Important Notes' })}
                </h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    {t('passwordChangeRequired', {
                      defaultValue: 'Employee must change password on first login'
                    })}
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    {t('credentialsSecure', {
                      defaultValue: 'Keep these credentials secure until shared with employee'
                    })}
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    {credentials.note?.includes('email') ?
                      t('emailSent', { defaultValue: 'Welcome email sent to employee' }) :
                      t('shareCredentialsSecurely', { defaultValue: 'Share credentials securely with employee' })
                    }
                  </li>
                </ul>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-semibold rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 hover:from-gray-600 hover:to-gray-700"
              >
                {t('close', { defaultValue: 'Close' })}
              </button>
              <button
                onClick={() => {
                  copyToClipboard(
                    `Employee ID: ${credentials.employeeId}\nEmail: ${credentials.email}\nPassword: ${credentials.temporaryPassword}`,
                    'All credentials'
                  );
                }}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 hover:from-blue-600 hover:to-purple-700"
              >
                {t('copyAll', { defaultValue: 'Copy All' })}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeCredentialsModal;
